<template>
    <div v-if="data">
        <SkillView :skill="data" />
    </div>
</template>

<script setup lang="ts">
import { skillTypeDict } from '#shared/types/dictionary'
import { type Skill, SkillType } from '#shared/types/Skill'
import { VisibilityType } from '#shared/types/visibility'
import { formatDate } from '#shared/utils'
import { useJsonLd } from '@/composables/head/useJsonLd'
import { useSharing } from '@/composables/sharing/useSharing'
import {
    absoluteUrl,
    buildBreadcrumbList,
    collapseWhitespace,
    composeDescription,
    htmlToPlainText,
    normalizeWebsiteUrl,
    SECTION_SOCIAL_IMAGES,
    withSiteName
} from '@/utils/structuredData'

// Composables
const route = useRoute()
const { getSharingUrl } = useSharing()

// Data
const skillSlug = route.params.slug as string

// Fetch data using server API (works on both SSR and client)
const { data, error, status } = await useFetch<Skill>(`/api/skills/${skillSlug}`, {
    key: `skill-detail-${skillSlug}`
})

// Handle hidden visibility redirect
if (data.value?.visibility === VisibilityType.hidden) {
    navigateTo({ path: '/ponuka-nie-je-aktualne-dostupna',
        query: {
            type: 'skills',
            id: skillSlug
        }
    })
}

// Handle error
if (error.value) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Skill not found'
    })
}

// Computed for template compatibility
const skill = computed(() => data.value)

// SEO
const skillName = computed(() => collapseWhitespace(skill.value?.title))
const skillTypeLabel = computed(() => skillTypeDict[skill.value?.type as SkillType] || 'Vzdelávanie')
const orgName = computed(() => collapseWhitespace(skill.value?.orgMeta?.title))
// Only published items are indexed and marked up; unlisted ones open by direct link only.
// Items older than the visibility field have none and were always public.
const isPublicSkill = computed(() => {
    const visibility = skill.value?.visibility
    return !!skill.value && (!visibility || visibility === VisibilityType.published)
})

// "<Názov> – kurz | GROWNi.sk" with the type as the site labels it (kurz, udalosť, skupina, …),
// unless the name already says it ("Kurz Junior programátora").
const skillTitle = computed(() => {
    if (!skill.value) return withSiteName('Vzdelávanie')
    const type = skillTypeLabel.value.toLocaleLowerCase('sk')
    const named = skillName.value.toLocaleLowerCase('sk').includes(type)
    return withSiteName(named ? skillName.value : `${skillName.value} – ${type}`)
})

// Courses have no date; for the other types SkillViewParameters shows startDate (event day or deadline).
const skillDateText = computed(() => {
    if (!skill.value?.startDate || skill.value.type === SkillType.kurz) return ''
    const date = formatDate(skill.value.startDate)
    return skill.value.type === SkillType.event ? date : `prihlášky do ${date}`
})

// "Udalosť od Lepšia s.r.o., 8. 12. 2026."
const skillLead = computed(() => {
    const details = [orgName.value ? `od ${orgName.value}` : '', skillDateText.value].filter(Boolean).join(', ')
    return `${skillTypeLabel.value}${details ? ` ${details}` : ''}.`
})

// The short description shown under the title, else the long content.
const skillSummary = computed(() => htmlToPlainText(skill.value?.desc) || htmlToPlainText(skill.value?.content))

const skillMetaDescription = computed(() => composeDescription([skillLead.value, skillSummary.value]))

const skillImage = computed(() => {
    return skill.value?.image ? getSharingUrl('skills', skill.value.image) : SECTION_SOCIAL_IMAGES.skills
})

const canonicalUrl = computed(() => absoluteUrl(`/vzdelavanie/${skill.value?.slug || skillSlug}`))

const skillOrganization = computed(() => (orgName.value ? { '@type': 'Organization', name: orgName.value } : undefined))

/** Event start in Slovak time: the day from startDate plus the "HH:MM" time when it is set. */
function getEventStart(current: Skill): string | undefined {
    if (!current.startDate) return undefined
    const day = formatDate(current.startDate, { format: 'YYYY-MM-DD' })
    const time = /^\d{1,2}:\d{2}$/.test(current.time?.trim() || '') ? current.time.trim().padStart(5, '0') : ''
    return time ? `${day}T${time}:00${formatDate(current.startDate, { format: 'Z' })}` : day
}

/**
 * Event for dated events, Course for courses. An Event needs a location to be valid for
 * Google, so events without a place or an online link get no Event markup.
 */
const skillJsonLd = computed(() => {
    const current = skill.value
    if (!current || !isPublicSkill.value) return null

    const common = {
        name: skillName.value,
        description: composeDescription([skillSummary.value || skillLead.value], 500),
        image: current.image,
        url: canonicalUrl.value
    }

    if (current.type === SkillType.kurz) {
        return { '@type': 'Course', ...common, provider: skillOrganization.value }
    }

    if (current.type !== SkillType.event) return null

    const onlineUrl = normalizeWebsiteUrl(current.placeOnline)
    const address = collapseWhitespace(current.placeAddress)
    const startDate = getEventStart(current)
    if (!startDate || (!onlineUrl && !address)) return null

    const locations = [
        address ? { '@type': 'Place', name: address, address } : undefined,
        onlineUrl ? { '@type': 'VirtualLocation', url: onlineUrl } : undefined
    ].filter(Boolean)
    const attendanceMode =
        address && onlineUrl ? 'MixedEventAttendanceMode' : onlineUrl ? 'OnlineEventAttendanceMode' : 'OfflineEventAttendanceMode'

    return {
        '@type': 'Event',
        ...common,
        startDate,
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: `https://schema.org/${attendanceMode}`,
        location: locations.length === 1 ? locations[0] : locations,
        organizer: skillOrganization.value,
        // Price as SkillViewLayoutTop shows it ("Bezplatne" for 0).
        offers:
            typeof current.price === 'number'
                ? { '@type': 'Offer', price: current.price, priceCurrency: 'EUR', url: canonicalUrl.value }
                : undefined
    }
})

useJsonLd(() => [
    skillJsonLd.value,
    isPublicSkill.value &&
        buildBreadcrumbList([
            { name: 'Domov', path: '/' },
            { name: 'Vzdelávanie', path: '/vzdelavanie' },
            { name: skillName.value, path: canonicalUrl.value }
        ])
])

useHead(() => ({
    title: skillTitle.value,
    link: [
        // `key` dedupes against the site-wide canonical set in app.vue
        { rel: 'canonical', key: 'canonical', href: canonicalUrl.value }
    ],
    meta: [
        ...(skill.value && !isPublicSkill.value ? [{ name: 'robots', content: 'noindex, follow' }] : []),
        { name: 'description', content: skillMetaDescription.value },
        { property: 'og:title', content: skillTitle.value },
        { property: 'og:description', content: skillMetaDescription.value },
        { property: 'og:image', content: skillImage.value },
        { property: 'og:image:alt', content: skillName.value },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: canonicalUrl.value }
    ]
}))

// Set dynamic breadcrumb title when skill data is available
watch(skill, (newSkill) => {
    if (newSkill?.title && process.client) {
        nextTick(() => {
            const { $dynamicTitle } = useNuxtApp()
            if ($dynamicTitle) {
                $dynamicTitle(newSkill.title)
            }
        })
    }
}, { immediate: true })
</script>
